/* Created by Interactive 63 - https://interactive63.com */
/**
 * The Look button and its panel: dark or light theme, the accent colour and the keyframe colour.
 * Changes apply straight away and are remembered.
 */
import {
  ACCENTS,
  KEYFRAME_HUES,
  applyAccent,
  applyKeyframeColour,
  applyTheme,
  currentAccent,
  currentKeyframeColour,
  currentTheme,
  resolveKeyframeHue,
  type AccentId,
  type KeyframeColourId,
  type Theme,
} from '../appearance';
import { h } from '../ui/dom';
import { createPopover } from '../ui/popover';

const THEMES: { id: Theme; label: string }[] = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
];

/** Creates the Look button with its panel attached next to it. */
export function createAppearanceMenu(): HTMLElement {
  const button = h(
    'button',
    {
      type: 'button',
      'aria-haspopup': 'dialog',
      title: 'Theme, accent colour and keyframe colour',
    },
    'Look',
  );
  const content = h('div', { class: 'appearance-panel' });
  const popover = createPopover(button, content, 'Look');

  /** Builds a row of radio buttons for one setting. */
  const group = <T extends string>(
    heading: string,
    options: { id: T; label: string; swatch?: string }[],
    current: T,
    choose: (id: T) => void,
  ): HTMLElement =>
    h(
      'div',
      { class: 'appearance-group', role: 'radiogroup', 'aria-label': heading },
      h('span', { class: 'appearance-heading' }, heading),
      h(
        'div',
        { class: 'appearance-options' },
        ...options.map((option) => {
          const radio = h(
            'button',
            {
              type: 'button',
              role: 'radio',
              class: option.swatch ? 'appearance-swatch' : 'appearance-choice',
              'aria-checked': String(option.id === current),
              'aria-label': option.label,
              title: option.label,
            },
            option.swatch
              ? h('span', { class: 'swatch-dot', style: { background: option.swatch } })
              : option.label,
          );
          radio.addEventListener('click', () => {
            choose(option.id);
            render();
          });
          return radio;
        }),
      ),
    );

  /** Rebuilds the panel from the saved choices. */
  function render(): void {
    const theme = currentTheme();
    const accent = currentAccent();
    const keyframe = currentKeyframeColour();
    const autoHue = KEYFRAME_HUES[resolveKeyframeHue('auto', accent)].label;
    const keyframeOptions: { id: KeyframeColourId; label: string }[] = [
      { id: 'auto', label: `Match accent (${autoHue})` },
      ...Object.entries(KEYFRAME_HUES).map(([id, hue]) => ({
        id: id as KeyframeColourId,
        label: hue.label,
      })),
    ];
    const select = h(
      'select',
      { 'aria-label': 'Keyframe colour' },
      ...keyframeOptions.map((option) =>
        h('option', { value: option.id, selected: option.id === keyframe }, option.label),
      ),
    );
    select.addEventListener('change', () => {
      applyKeyframeColour(select.value as KeyframeColourId);
      render();
    });
    content.replaceChildren(
      group('Theme', THEMES, theme, applyTheme),
      group(
        'Accent',
        (Object.keys(ACCENTS) as AccentId[]).map((id) => ({
          id,
          label: ACCENTS[id].label,
          swatch: ACCENTS[id].dark.accent,
        })),
        accent,
        applyAccent,
      ),
      h(
        'label',
        { class: 'appearance-group' },
        h('span', { class: 'appearance-heading' }, 'Keyframes'),
        select,
      ),
    );
  }

  button.addEventListener('click', () => {
    if (popover.isOpen()) {
      popover.close();
      return;
    }
    render();
    popover.open();
  });
  return h('span', { class: 'menu-wrap' }, button, popover.el);
}
